import { Stack } from '@mantine/core';
import { useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useTravelRequestEdit } from '@/module/Travel/Request/model/TravelRequestQuery';
import {
  TravelRequestSchema,
  defaultTravelRequestValues,
  toTravelRequestRequest,
  toTravelRequestSchema,
  travelRequestSchema,
} from '@/module/Travel/Request/model/TravelRequestSchema';
import { TravelRequest, travelRequestPermissions } from '@/module/Travel/Request/model/TravelRequestTypes';

import { NoContentPlaceholder } from '@/core/components';

import { useCurrentUser } from '@feature/security';
import { useAppNotification } from '@feature/utils';

import { TravelRequestForm } from './TravelRequestForm';
import { isTravelPaperTitleTakenError } from './TravelRequestFormUtils';

interface TravelRequestEditProps {
  request: TravelRequest;
}

export function TravelRequestEdit(props: TravelRequestEditProps) {
  const { request } = props;
  const { t } = useTranslation();
  const { mutate, isPending, isError, isSuccess } = useTravelRequestEdit(request.id);

  const notification = useAppNotification();
  const navigate = useNavigate();
  const [refusedPaperTitle, setRefusedPaperTitle] = useState<string | null>(null);
  const onSubmit = (values: TravelRequestSchema) => {
    mutate(toTravelRequestRequest(values), {
      onError: error => {
        if (isTravelPaperTitleTakenError(error)) {
          setRefusedPaperTitle(values.paperTitle?.trim() ?? null);
        }
        notification.error(error);
      },
      onSuccess: () => {
        notification.success();
        navigate({ to: '..' });
      },
    });
  };

  const values = useMemo(() => toTravelRequestSchema(request), [request]);
  const currentUser = useCurrentUser();
  const isRequester = request.requester.id === currentUser.id;

  if (!travelRequestPermissions(request.status, isRequester, currentUser.currentRole).canEdit) {
    return <NoContentPlaceholder title={t('notification.error.code.TRAVEL_REQUEST_NOT_EDITABLE')} />;
  }

  return (
    <Stack>
      <TravelRequestForm
        title={t('travel.requests.form.title.edit')}
        requestId={request.id}
        request={request}
        refusedPaperTitle={refusedPaperTitle}
        schema={travelRequestSchema}
        defaultValues={defaultTravelRequestValues}
        values={values}
        isSubmitting={isPending}
        isError={isError}
        isSuccess={isSuccess}
        onSubmit={onSubmit}
        onCancel={() => navigate({ to: '..' })}
      />
    </Stack>
  );
}
